import { useEffect, useState, useCallback } from "react";
import { Coins, Send, Loader2, X, Check, Clock, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

function timeLeft(iso) {
    if (!iso) return "";
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
}

export default function Transfer() {
    const { user } = useAuth();
    const [credits, setCredits] = useState(null);
    const [inbox, setInbox] = useState([]);
    const [outbox, setOutbox] = useState([]);
    const [toUsername, setToUsername] = useState("");
    const [amount, setAmount] = useState(10);
    const [expire24h, setExpire24h] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const loadAll = useCallback(async () => {
        try {
            const [bal, inb, out] = await Promise.all([
                api.get("/payments/i2v/balance"),
                api.get("/credits/transfers/inbox"),
                api.get("/credits/transfers/outbox"),
            ]);
            setCredits(bal.data.credits);
            setInbox(inb.data);
            setOutbox(out.data);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { if (user) loadAll(); }, [user, loadAll]);
    useEffect(() => {
        const t = setInterval(loadAll, 15000);
        return () => clearInterval(t);
    }, [loadAll]);

    const send = async (e) => {
        e.preventDefault();
        setError(""); setSuccess("");
        if (!toUsername.trim() || amount < 1) { setError("Fill in username and amount"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/credits/transfer", {
                to_username: toUsername.trim(),
                amount: Number(amount),
                expire_24h: expire24h,
                note: note.trim() || null,
            });
            setSuccess(`Sent ${data.transfer.amount} cr to @${data.transfer.to_username}`);
            setToUsername(""); setAmount(10); setNote(""); setExpire24h(false);
            await loadAll();
        } catch (e2) {
            setError(e2?.response?.data?.detail || "Transfer failed");
        } finally {
            setBusy(false);
        }
    };

    const claim = async (tid) => {
        try { await api.post(`/credits/transfers/${tid}/claim`); await loadAll(); }
        catch (e) { setError(e?.response?.data?.detail || "Couldn't claim"); }
    };

    const cancel = async (tid) => {
        try { await api.post(`/credits/transfers/${tid}/cancel`); await loadAll(); }
        catch (e) { setError(e?.response?.data?.detail || "Couldn't cancel"); }
    };

    return (
        <div className="min-h-screen bg-cream">
            <div className="max-w-4xl mx-auto px-6 py-10">
                <div className="mb-8">
                    <h1 className="font-heading font-black text-5xl text-ink tracking-tighter">Transfer credits</h1>
                    <p className="font-body text-ink/60 mt-1">Send credits to another user by their username. Optional 24-hour claim window.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <form onSubmit={send} className="brutal-card p-6 bg-vibrantyellow" data-testid="transfer-form">
                        <div className="flex items-center justify-between mb-4">
                            <span className="font-heading font-black text-lg text-ink">Send</span>
                            <div className="bg-white border-2 border-ink rounded-lg px-2 py-1 flex items-center gap-1">
                                <Coins className="w-3.5 h-3.5" strokeWidth={2.5} />
                                <span className="font-heading font-black text-sm text-ink tabular-nums">{credits ?? 0}</span>
                            </div>
                        </div>
                        <label className="block font-heading font-bold text-[10px] uppercase tracking-widest text-ink/70 mb-1">To (username)</label>
                        <input
                            type="text"
                            value={toUsername}
                            onChange={(e) => setToUsername(e.target.value.replace(/[^\w-]/g, "").toLowerCase())}
                            placeholder="alice"
                            required
                            data-testid="transfer-to"
                            className="brutal-card w-full py-2 px-3 bg-white text-ink font-body mb-3"
                        />
                        <label className="block font-heading font-bold text-[10px] uppercase tracking-widest text-ink/70 mb-1">Amount</label>
                        <input
                            type="number"
                            min={1}
                            max={100000}
                            value={amount}
                            onChange={(e) => setAmount(parseInt(e.target.value || "0", 10) || 0)}
                            required
                            data-testid="transfer-amount"
                            className="brutal-card w-full py-2 px-3 bg-white text-ink font-heading font-black text-lg mb-3"
                        />
                        <label className="block font-heading font-bold text-[10px] uppercase tracking-widest text-ink/70 mb-1">Note (optional)</label>
                        <input
                            type="text"
                            maxLength={140}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="thanks!"
                            data-testid="transfer-note"
                            className="brutal-card w-full py-2 px-3 bg-white text-ink font-body mb-3"
                        />
                        <label className="flex items-center gap-2 font-body text-sm text-ink mb-4 cursor-pointer" data-testid="transfer-expire-label">
                            <input
                                type="checkbox"
                                checked={expire24h}
                                onChange={(e) => setExpire24h(e.target.checked)}
                                data-testid="transfer-expire-toggle"
                                className="w-4 h-4"
                            />
                            <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />
                            <span>Expire in 24h if unclaimed (auto-refund)</span>
                        </label>
                        {error && <p data-testid="transfer-error" className="font-body font-semibold text-sm text-vibrantpink mb-2">{error}</p>}
                        {success && <p data-testid="transfer-success" className="font-body font-semibold text-sm text-emerald-700 mb-2">{success}</p>}
                        <button type="submit" disabled={busy} data-testid="transfer-submit" className="brutal-btn-primary w-full">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : <><Send className="w-4 h-4 mr-2" strokeWidth={2.5} /> Send transfer</>}
                        </button>
                    </form>

                    <div className="space-y-4">
                        <div className="brutal-card p-5 bg-white" data-testid="transfer-inbox">
                            <div className="flex items-center gap-2 mb-3">
                                <ArrowDownLeft className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />
                                <span className="font-heading font-black text-ink">Inbox ({inbox.length})</span>
                            </div>
                            {inbox.length === 0 ? (
                                <p className="font-body text-sm text-ink/50">No pending transfers</p>
                            ) : (
                                <ul className="space-y-2">
                                    {inbox.map((t) => (
                                        <li key={t.id} className="brutal-card p-3 bg-cream flex items-center gap-2" data-testid={`transfer-inbox-${t.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-heading font-black text-ink text-sm">
                                                    +{t.amount} cr from <span className="text-vibrantorange">@{t.from_username}</span>
                                                </div>
                                                {t.note && <div className="font-body text-xs text-ink/60 italic truncate">&ldquo;{t.note}&rdquo;</div>}
                                                {t.expires_at && <div className="font-body text-[11px] text-vibrantpink font-semibold">{timeLeft(t.expires_at)}</div>}
                                            </div>
                                            <button onClick={() => claim(t.id)} className="brutal-btn-primary py-1 px-3 text-xs" data-testid={`transfer-claim-${t.id}`}>
                                                <Check className="w-3 h-3 mr-1" strokeWidth={3} /> Claim
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="brutal-card p-5 bg-white" data-testid="transfer-outbox">
                            <div className="flex items-center gap-2 mb-3">
                                <ArrowUpRight className="w-4 h-4 text-vibrantorange" strokeWidth={2.5} />
                                <span className="font-heading font-black text-ink">Sent ({outbox.length})</span>
                            </div>
                            {outbox.length === 0 ? (
                                <p className="font-body text-sm text-ink/50">You haven&apos;t sent anything yet</p>
                            ) : (
                                <ul className="space-y-2 max-h-64 overflow-y-auto">
                                    {outbox.slice(0, 20).map((t) => (
                                        <li key={t.id} className="brutal-card p-2.5 bg-cream text-sm" data-testid={`transfer-outbox-${t.id}`}>
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-heading font-black text-ink">
                                                        -{t.amount} cr → @{t.to_username}
                                                    </div>
                                                    <div className="font-body text-[11px] text-ink/60 capitalize">{t.status} {t.expires_at && t.status === "pending" ? `· ${timeLeft(t.expires_at)}` : ""}</div>
                                                </div>
                                                {t.status === "pending" && (
                                                    <button onClick={() => cancel(t.id)} className="brutal-btn-white py-1 px-2 text-[10px]" data-testid={`transfer-cancel-${t.id}`}>
                                                        <X className="w-3 h-3" strokeWidth={3} />
                                                    </button>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
